// Formatierung von Nachrichten wie im Messenger: *fett*, _kursiv_,
// ~durchgestrichen~, `Code`, ```Block```, Zitate mit „> “, Listen mit „- “,
// „* “ oder „1. “ und klickbare Links. Liefert einen kleinen Baum, den Web und
// App jeweils selbst darstellen – nie HTML, damit nichts eingeschleust wird.

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'b' | 'i' | 's'; c: Inline[] }
  | { t: 'code'; v: string }
  | { t: 'link'; href: string; v: string }
  /** @Erwähnung einer Person (id wie in `message.mentions`) */
  | { t: 'mention'; id: string; v: string };

/** Bekannte Namen für @Erwähnungen; nur diese werden hervorgehoben. */
export interface RichTextOptions {
  mentions?: { id: string; name: string }[];
}

export type Block =
  /** Gewöhnliche Zeilen, jede Zeile eine Liste von Inline-Teilen */
  | { t: 'lines'; lines: Inline[][] }
  | { t: 'quote'; lines: Inline[][] }
  | { t: 'ul'; items: Inline[][] }
  | { t: 'ol'; start: number; items: Inline[][] }
  | { t: 'pre'; v: string };

// Adressen enden nicht auf Satzzeichen („Siehe https://x.de.“)
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"]*[^\s<>".,:;!?'")\]]/gi;
const EMAIL_RE = /\b[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[a-z]{2,}\b/gi;
// Telefonnummern mit Vorwahl (+49 …, 0761 …); Punkte ausgeschlossen, damit Daten heil bleiben
const PHONE_RE = /(?<![\p{L}\p{N}+\/.-])(?:\+|0)\d[\d \-\/()]{5,20}\d(?![\p{L}\p{N}\/.-])/gu;

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const DELIMS: { d: string; t: 'b' | 'i' | 's' }[] = [
  { d: '\\*', t: 'b' },
  { d: '_', t: 'i' },
  { d: '~', t: 's' },
];

// Öffnendes Zeichen am Wortanfang, direkt gefolgt von Text; schließendes
// direkt nach Text und vor einem Wortende. So bleiben snake_case und 2*3*4 heil.
const MARK_RE = DELIMS.map(({ d, t }) => ({
  t,
  re: new RegExp(`(^|[^\\p{L}\\p{N}${d}])${d}(?=[^\\s${d}])([^\\n]*?[^\\s${d}])${d}(?![\\p{L}\\p{N}])`, 'u'),
}));
const CODE_RE = /(^|[^`])`([^`\n]+)`(?!`)/;

/** Text einer Zeile in Links, Erwähnungen und Formatierungen zerlegen. */
export function parseInline(text: string, opts: RichTextOptions = {}): Inline[] {
  const out: Inline[] = [];
  const found: { start: number; end: number; node: Inline }[] = [];
  const free = (start: number, end: number) => !found.some((f) => start < f.end && end > f.start);
  for (const m of text.matchAll(URL_RE)) {
    const v = m[0];
    found.push({ start: m.index!, end: m.index! + v.length, node: { t: 'link', href: v.startsWith('www.') ? `https://${v}` : v, v } });
  }
  for (const m of text.matchAll(EMAIL_RE)) {
    if (!free(m.index!, m.index! + m[0].length)) continue;
    found.push({ start: m.index!, end: m.index! + m[0].length, node: { t: 'link', href: `mailto:${m[0]}`, v: m[0] } });
  }
  for (const m of text.matchAll(PHONE_RE)) {
    const digits = m[0].replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15 || !free(m.index!, m.index! + m[0].length)) continue;
    const href = `tel:${m[0].startsWith('+') ? '+' : ''}${digits}`;
    found.push({ start: m.index!, end: m.index! + m[0].length, node: { t: 'link', href, v: m[0] } });
  }
  // Längere Namen zuerst, damit „@Anna Maria“ nicht als „@Anna“ endet
  const people = [...(opts.mentions ?? [])].filter((p) => p.name.trim()).sort((a, b) => b.name.length - a.name.length);
  if (people.length && text.includes('@')) {
    const re = new RegExp(`(^|[^\\p{L}\\p{N}@])@(${people.map((p) => escapeRe(p.name)).join('|')})(?![\\p{L}\\p{N}])`, 'giu');
    for (const m of text.matchAll(re)) {
      const start = m.index! + m[1]!.length;
      const end = start + 1 + m[2]!.length;
      const person = people.find((p) => p.name.toLowerCase() === m[2]!.toLowerCase());
      if (person && free(start, end)) found.push({ start, end, node: { t: 'mention', id: person.id, v: `@${m[2]}` } });
    }
  }
  found.sort((a, b) => a.start - b.start);
  let pos = 0;
  for (const f of found) {
    if (f.start > pos) out.push(...formatText(text.slice(pos, f.start)));
    out.push(f.node);
    pos = f.end;
  }
  if (pos < text.length) out.push(...formatText(text.slice(pos)));
  return out;
}

/** Formatierungen ohne Links; die früheste Markierung gewinnt, innen rekursiv. */
function formatText(text: string): Inline[] {
  if (!text) return [];
  let best: { start: number; end: number; node: Inline } | null = null;
  const code = CODE_RE.exec(text);
  if (code) {
    const start = code.index + code[1]!.length;
    best = { start, end: start + code[2]!.length + 2, node: { t: 'code', v: code[2]! } };
  }
  for (const { t, re } of MARK_RE) {
    const m = re.exec(text);
    if (!m) continue;
    const start = m.index + m[1]!.length;
    if (best && best.start <= start) continue;
    best = { start, end: start + m[2]!.length + 2, node: { t, c: formatText(m[2]!) } };
  }
  if (!best) return [{ t: 'text', v: text }];
  const out: Inline[] = [];
  if (best.start > 0) out.push({ t: 'text', v: text.slice(0, best.start) });
  out.push(best.node);
  out.push(...formatText(text.slice(best.end)));
  return out;
}

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBER = /^\s*(\d{1,3})[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;

/** Ganze Nachricht in Blöcke zerlegen. */
export function parseRichText(src: string, opts: RichTextOptions = {}): Block[] {
  const blocks: Block[] = [];
  const parts = src.split(/```/);
  // Ungerade Teile stehen zwischen ``` … ``` – nur wenn der Block auch geschlossen ist
  const closed = parts.length % 2 === 1;
  const isPre = (i: number) => i % 2 === 1 && (closed || i < parts.length - 1);
  parts.forEach((part, i) => {
    if (isPre(i)) {
      blocks.push({ t: 'pre', v: part.replace(/^\n/, '').replace(/\n$/, '') });
      return;
    }
    let text = i % 2 === 1 ? '```' + part : part;
    // Zeilenumbrüche direkt an einem Block gehören zum Block, nicht zum Text
    if (i > 0) text = text.replace(/^\n/, '');
    if (isPre(i + 1)) text = text.replace(/\n$/, '');
    lineBlocks(text, blocks, opts);
  });
  return blocks.filter((b) => !(b.t === 'lines' && b.lines.length === 0));
}

function lineBlocks(text: string, blocks: Block[], opts: RichTextOptions) {
  if (!text) return;
  const push = (b: Block) => {
    const last = blocks[blocks.length - 1];
    if (last && last.t === b.t && b.t !== 'pre') {
      if (b.t === 'lines' || b.t === 'quote') (last as typeof b).lines.push(...b.lines);
      else if (b.t === 'ul' || b.t === 'ol') (last as typeof b).items.push(...b.items);
      return;
    }
    blocks.push(b);
  };
  for (const line of text.split('\n')) {
    let m: RegExpMatchArray | null;
    if ((m = line.match(QUOTE))) push({ t: 'quote', lines: [parseInline(m[1]!, opts)] });
    // „* “ mit Leerzeichen ist eine Liste, „*fett*“ nicht
    else if ((m = line.match(BULLET))) push({ t: 'ul', items: [parseInline(m[1]!, opts)] });
    else if ((m = line.match(NUMBER))) push({ t: 'ol', start: Number(m[1]), items: [parseInline(m[2]!, opts)] });
    else push({ t: 'lines', lines: [parseInline(line, opts)] });
  }
}

/**
 * Besteht die Nachricht nur aus ein bis drei Emoji, zeigen Messenger sie groß.
 * Liefert deren Anzahl, sonst 0.
 */
export function emojiOnly(src: string): number {
  const text = src.replace(/\s+/g, '');
  if (!text || text.length > 40) return 0;
  const Segmenter = (Intl as { Segmenter?: new (l?: string, o?: { granularity: 'grapheme' }) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter;
  const graphemes = Segmenter ? [...new Segmenter(undefined, { granularity: 'grapheme' }).segment(text)].map((s) => s.segment) : [...text];
  if (graphemes.length > 3) return 0;
  return graphemes.every((g) => /\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(g)) ? graphemes.length : 0;
}
